import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { isIOS, isStandalone } from "../lib/pwa";
import { getLocalConsent, saveLocalConsent } from "../lib/consent";

/** Installed PWA on iPhone/iPad: the email link opens Safari, not the app → lead with the code. */
export const codeFirst = () => isIOS() && isStandalone();

const friendly = (error) =>
  error.status === 429 || /rate limit|too many|60 seconds/i.test(error.message)
    ? "Too many codes requested — wait a few minutes and try again."
    : error.message;

/* Passwordless email OTP / magic-link state machine shared by /auth and the landing-page modal. */
export const useEmailAuth = ({ onSuccess, initialEmail = "", initialSent = false } = {}) => {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(initialSent);
  const [showCode, setShowCode] = useState(() => codeFirst());
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);

  useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => setCooldown((x) => Math.max(0, x - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // The OTP/magic link is only dispatched after an explicit, recorded opt-in for this email (UAE PDPL / India DPDP).
  const dispatchOtp = async () => {
    setError(""); setInfo(""); setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}/auth` } });
    setBusy(false);
    if (error) { if (error.status === 429) setCooldown(60); return setError(friendly(error)); }
    setSent(true); setCooldown(60);
    setInfo(codeFirst() ? "Email sent. Enter the code from it below." : "Email sent. Tap the link inside to sign in.");
  };
  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    if (await getLocalConsent(email)) return dispatchOtp();
    setConsentOpen(true);
  };
  const confirmConsent = async () => { await saveLocalConsent(email); setConsentOpen(false); await dispatchOtp(); };

  const verify = async (e, token = code) => {
    e?.preventDefault();
    if (!/^\d{6,8}$/.test(token)) return setError("Enter the code from the email.");
    setError(""); setBusy(true);
    const { error } = await supabase.auth.verifyOtp({ email: email.trim(), token, type: "email" });
    setBusy(false);
    if (error) return setError(friendly(error));
    onSuccess?.();
  };

  const changeEmail = () => { setSent(false); setCode(""); setError(""); setInfo(""); setShowCode(codeFirst()); };
  return { email, setEmail, code, setCode, sent, showCode, setShowCode, cooldown, error, info, busy, sendCode, verify, changeEmail, iosApp: codeFirst(), consentOpen, closeConsent: () => setConsentOpen(false), confirmConsent };
};
